import React, { useState } from 'react';
import {
	Box,
	Heading,
	Text,
	VStack,
	HStack,
	Icon,
	Divider,
	Center,
	Spinner,
	useDisclosure,
	Alert,
	AlertIcon,
	Badge,
	Button,
} from '@chakra-ui/react';
import { FaLock, FaArrowUp, FaArrowLeft } from 'react-icons/fa';
import { useFeatureGate } from '../../../services/Contexts/FeatureGateContext.jsx';
import { usePlans } from '../../../hooks/usePlan.js';
import { useSubscription } from '../../../hooks/useSubscription.js';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext.jsx';
import SubscriptionPlanMatrix from '../../Auth/SubscriptionPlan/SubscriptionPlanMatrix.jsx';
import SubscriptionModal from '../../Auth/SubscriptionPlan/EmbeddedCheckout/SubscriptionModal.jsx';

const FeatureGatePage = () => {
	const { errorData, close } = useFeatureGate();
	const { companyId } = useAuthenticatedUserInfo();
	const { data: plans, isLoading: loadingPlans } = usePlans();
	const { data: subscription, refetch } = useSubscription(companyId);
	const [isYearly, setIsYearly] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState(null);
	const {
		isOpen: isCheckoutOpen,
		onOpen: onCheckoutOpen,
		onClose: onCheckoutClose,
	} = useDisclosure();

	const isLimitReached = errorData?.code === 'FEATURE_LIMIT_REACHED';
	const currentPlanName = errorData?.currentPlan || 'your current plan';

	const handleSelectPlan = (plan) => {
		setSelectedPlan({
			...plan,
			stripePriceId: isYearly
				? plan.stripePriceIdYearly
				: plan.stripePriceIdMonthly,
		});
		onCheckoutOpen();
	};

	const handleSubscriptionSuccess = () => {
		onCheckoutClose();
		refetch();
		close();
	};

	return (
		<>
			<Box w='full' px={{ base: 4, md: 8 }} py={8} maxW='1200px' mx='auto'>
				<VStack spacing={6} align='stretch'>
					{/* Back button */}
					{/* <Box>
						<Button
							leftIcon={<FaArrowLeft />}
							variant='ghost'
							colorScheme='gray'
							size='sm'
							onClick={close}>
							Go back
						</Button>
					</Box> */}

					{/* Header */}
					<Center>
						<VStack spacing={3}>
							<Box
								p={4}
								bg={isLimitReached ? 'orange.50' : 'blue.50'}
								borderRadius='full'>
								<Icon
									as={isLimitReached ? FaArrowUp : FaLock}
									boxSize={8}
									color={isLimitReached ? 'orange.500' : 'blue.500'}
								/>
							</Box>
							<Heading as='h2' size='md' textAlign='center' color='gray.800'>
								{isLimitReached
									? "You've reached your plan limit"
									: 'This feature requires an upgrade'}
							</Heading>
							<HStack>
								<Badge colorScheme='gray' fontSize='sm' px={3} py={1}>
									Current plan: {currentPlanName}
								</Badge>
							</HStack>
							<Text fontSize='sm' textAlign='center' color='gray.500' maxW='460px'>
								{errorData?.error || 'Please upgrade your plan to access this feature.'}
							</Text>
						</VStack>
					</Center>

					<Divider />

					{/* Billing toggle */}
					<Center>
						<HStack
							spacing={0}
							borderRadius='full'
							overflow='hidden'
							border='1px'
							borderColor='gray.200'
							maxW='320px'
							w='full'
							position='relative'>
							<Box
								position='absolute'
								top={0}
								left={isYearly ? '50%' : '0'}
								w='50%'
								h='full'
								bg='orange.400'
								borderRadius='full'
								transition='left 0.3s ease'
								zIndex={1}
							/>
							<Box
								as='button'
								flex={1}
								py={2}
								px={4}
								color={isYearly ? 'gray.600' : 'white'}
								fontWeight={isYearly ? 'normal' : 'semibold'}
								fontSize='sm'
								cursor='pointer'
								position='relative'
								zIndex={2}
								transition='color 0.3s'
								onClick={() => setIsYearly(false)}>
								Monthly
							</Box>
							<Box
								as='button'
								flex={1}
								py={2}
								px={4}
								color={!isYearly ? 'gray.600' : 'white'}
								fontWeight={!isYearly ? 'normal' : 'semibold'}
								fontSize='sm'
								cursor='pointer'
								position='relative'
								zIndex={2}
								transition='color 0.3s'
								onClick={() => setIsYearly(true)}>
								Annual
							</Box>
						</HStack>
					</Center>

					{/* Plan matrix */}
					{loadingPlans ? (
						<Center py={8}>
							<VStack spacing={3}>
								<Spinner size='lg' color='blue.500' />
								<Text fontSize='sm' color='gray.500'>
									Loading plans...
								</Text>
							</VStack>
						</Center>
					) : (
						<Box
							w='full'
							overflowX='auto'
							borderRadius='lg'
							border='1px'
							borderColor='gray.200'
							boxShadow='sm'>
							<SubscriptionPlanMatrix
								plans={plans || []}
								onSelectPlan={handleSelectPlan}
								showTitle={true}
								showPrice={true}
								isYearly={isYearly}
								bgColor='white'
								currentSubscription={subscription}
							/>
						</Box>
					)}

					<Alert status='info' borderRadius='md' fontSize='sm'>
						<AlertIcon />
						Upgrade now and get instant access to all features included in your new
						plan.
					</Alert>
				</VStack>
			</Box>

			{/* Checkout modal */}
			<SubscriptionModal
				isOpen={isCheckoutOpen}
				onClose={onCheckoutClose}
				plan={selectedPlan}
				isYearly={isYearly}
				onSubscriptionSuccess={handleSubscriptionSuccess}
				currentSubscription={subscription}
				isUpgrade={!!subscription?.hasActiveSubscription}
			/>
		</>
	);
};

export default FeatureGatePage;
